import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRequest, get, patch, post } from 'api/base';
import { influenzaVaccineKeys } from 'hooks/useInfluenzaVaccines';
import { preventionAlertKeys } from 'hooks/usePreventionAlerts';
import type {
  CreateImmunizationPayload,
  Immunization,
  ImmunizationDraft,
  UpdateImmunizationPayload,
} from 'types/immunization';

type ImmunizationsResponse = {
  immunizations: Immunization[];
};

export const immunizationKeys = {
  byInteraction: (interactionId?: string) => ['immunizations', interactionId] as const,
};

export const draftToPayload = (
  draft: ImmunizationDraft,
  interactionId: string,
  medatixxId: string
): CreateImmunizationPayload => ({
  interactionId,
  medatixxId,
  vaccineName: draft.vaccineName,
  batchNumber: draft.batchNumber.trim(),
  reason: draft.reason,
  diagnosisSelected: draft.diagnosisSelected,
  diagnosisCategory: draft.diagnosisCategory,
  diagnosisSicherheit: draft.diagnosisSicherheit,
  diagnosisSeite: draft.diagnosisSeite,
});

export function useCreateImmunization() {
  const queryClient = useQueryClient();
  return useMutation<Immunization, Error, CreateImmunizationPayload>({
    mutationFn: (payload) => post('immunizations', payload) as Promise<Immunization>,
    onSuccess: (_created, { interactionId, medatixxId }) => {
      // The summary may open right after this; a list cached earlier must not outlive the store.
      void queryClient.invalidateQueries({ queryKey: preventionAlertKeys.all(medatixxId) });
      void queryClient.invalidateQueries({ queryKey: influenzaVaccineKeys.all });
      return queryClient.invalidateQueries({
        queryKey: immunizationKeys.byInteraction(interactionId),
        refetchType: 'all',
      });
    },
  });
}

export function useImmunizations(interactionId?: string, medatixxPatientId?: string | null) {
  const queryClient = useQueryClient();

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: immunizationKeys.byInteraction(interactionId) }),
      queryClient.invalidateQueries({ queryKey: preventionAlertKeys.all(medatixxPatientId) }),
      queryClient.invalidateQueries({ queryKey: influenzaVaccineKeys.all }),
    ]);

  const query = useQuery<ImmunizationsResponse>({
    queryKey: immunizationKeys.byInteraction(interactionId),
    queryFn: () =>
      get('immunizations', undefined, { interactionId }) as Promise<ImmunizationsResponse>,
    enabled: !!interactionId,
  });

  const update = useMutation<Immunization, Error, { id: number; data: UpdateImmunizationPayload }>({
    mutationFn: ({ id, data }) => patch('immunizations', data, id) as Promise<Immunization>,
    onSuccess: invalidate,
  });

  const remove = useMutation<void, Error, number>({
    mutationFn: (id) => deleteRequest('immunizations', id) as Promise<void>,
    onSuccess: invalidate,
  });

  return {
    immunizations: query.data?.immunizations ?? [],
    isLoading: query.isLoading,
    update,
    remove,
  };
}
